import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import type { BillingType } from '@pulsepoint/shared';
import { Link2, Plus } from 'lucide-react';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router';
import { useMe } from '../../api/auth';
import { useChargers, type Charger } from '../../api/chargers';
import { useDrivers } from '../../api/drivers';
import { useDashboard, useDashboardMonths } from '../../api/sessions';
import { usePermission } from '../../auth/permissions';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { PageHeader } from '../../components/PageHeader';
import { Stat, StatStrip } from '../../components/StatStrip';
import { StatusLabel, type StatusTone } from '../../components/StatusLabel';
import { firstName, formatAgo, greeting } from '../../lib/format';
import { placeOf } from '../../lib/place';
import { colours, radius } from '../../theme';
import { ChargerDialog } from '../chargers/ChargerDialog';
import { chargerName, connectorsOf } from '../chargers/status';
import { DriverDialog } from '../drivers/DriverDialog';
import { OnboardingLinksDialog } from '../drivers/OnboardingLinksDialog';
import { billedTo, kwhOf, whoCharged } from '../sessions/format';
import { SessionDialog } from '../sessions/SessionDialog';
import { useOrganisations } from '../../api/organisations';
import { useHomeChargingMonths } from '../powersensor/api'; // Powersensor is being retired
import { MonthlyChargingChart, type ChartSeries } from './MonthlyChargingChart';

const clock = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' });
const today = new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** "Docklands strata" or "Docklands strata · Bay 3" */
const where = (c: Charger) => [placeOf(c), c.bay ? `Bay ${c.bay}` : null].filter(Boolean).join(' · ');

interface Attention {
  key: string;
  tone: StatusTone;
  label: string;
  at?: string;
  title: string;
  detail: string;
  charger?: Charger;
  link?: { to: string; text: string };
}

/** Home: the chargers right now, this month's energy, live sessions and what needs attention. */
export function HomePage() {
  const { data: me } = useMe();
  const chargers = useChargers();
  const dash = useDashboard();
  const canRegister = usePermission('chargers.register');
  const canEditDrivers = usePermission('drivers.edit');
  const waitingDrivers = useDrivers('review', undefined, canEditDrivers);
  const dialog = useDialog();
  const phone = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));

  const all = chargers.data ?? [];
  const accepted = all.filter((c) => c.registration === 'accepted' && c.siteId);

  // Every socket, counted once, by what it's doing now.
  const counts = useMemo(() => {
    const out = { available: 0, charging: 0, faulted: 0, offline: 0, powerW: 0, faultedAt: new Set<string>(), offlineAt: new Set<string>() };
    for (const c of accepted) {
      for (const k of connectorsOf(c)) {
        if (k.status === 'Offline') {
          out.offline++;
          out.offlineAt.add(placeOf(c));
        } else if (k.status === 'Faulted' || k.status === 'Unavailable') {
          out.faulted++;
          out.faultedAt.add(placeOf(c));
        } else if (['Charging', 'SuspendedEV', 'SuspendedEVSE', 'Finishing'].includes(k.status) || c.activeSessions.some((s) => s.connectorId === k.connectorId)) {
          out.charging++;
        } else out.available++;
      }
      out.powerW += c.activeSessions.reduce((n, s) => n + (s.powerW ?? 0), 0);
    }
    return out;
  }, [accepted]);

  const live = accepted
    .flatMap((c) => c.activeSessions.map((s) => ({ c, s })))
    .sort((a, b) => b.s.startedAt.localeCompare(a.s.startedAt));

  const attention: Attention[] = [];
  for (const c of accepted) {
    const ks = connectorsOf(c);
    const fault = ks.find((k) => k.status === 'Faulted' || k.status === 'Unavailable');
    if (!c.isConnected) {
      attention.push({ key: c.id, tone: 'offline', label: 'Offline', at: c.lastSeenAt ?? undefined, title: `${chargerName(c)} · ${where(c)}`, detail: c.lastSeenAt ? `Last heard ${formatAgo(c.lastSeenAt)}.` : 'It has never connected.', charger: c });
    } else if (fault) {
      attention.push({
        key: c.id,
        tone: fault.status === 'Faulted' ? 'fault' : 'off',
        label: fault.status === 'Faulted' ? 'Faulted' : 'Unavailable',
        at: fault.at,
        title: `${chargerName(c)} · ${where(c)}`,
        detail: [fault.errorCode && fault.errorCode !== 'NoError' ? fault.errorCode : null, fault.info].filter(Boolean).join(': ') || (fault.status === 'Faulted' ? 'The charger reported a fault.' : 'The charger is set to unavailable.'),
        charger: c,
      });
    }
  }
  // Faults first, then the most recent.
  attention.sort((a, b) => (a.tone === 'fault' ? 0 : 1) - (b.tone === 'fault' ? 0 : 1) || (b.at ?? '').localeCompare(a.at ?? ''));
  const pending = all.filter((c) => c.registration === 'pending');
  if (canRegister && pending.length) {
    attention.unshift({ key: 'pending', tone: 'neutral', label: 'New charger', title: pending.length === 1 ? `${pending[0]!.chargePointId} is waiting` : `${pending.length} new chargers are waiting`, detail: 'Accept the ones you recognise.', link: { to: '/sites', text: 'Review' } });
  }
  const signUps = waitingDrivers.data?.length ?? 0;
  if (signUps) {
    attention.unshift({ key: 'drivers', tone: 'neutral', label: 'Drivers', title: `${plural(signUps, 'driver')} waiting for approval`, detail: 'They signed up with an onboarding link.', link: { to: '/drivers', text: 'Review' } });
  }

  const places = new Set(accepted.map((c) => c.siteId)).size;
  const needs = accepted.filter((c) => connectorsOf(c).some((k) => ['Offline', 'Faulted', 'Unavailable'].includes(k.status))).length;
  const lead = chargers.isPending ? ' ' : accepted.length === 0 ? 'No chargers yet.' : `${plural(accepted.length, 'charger')} in ${plural(places, 'place')}. ${needs ? `${needs} ${needs === 1 ? 'needs' : 'need'} attention.` : 'All are working.'}`;

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${firstName(me?.displayName ?? '')}.`}
        documentTitle="Home"
        lead={lead}
        actions={
          <Box sx={{ textAlign: { md: 'right' }, color: colours.graphite }}>
            <Typography>{today.format(new Date())}</Typography>
            {chargers.dataUpdatedAt > 0 && <Typography>Live, updated {clock.format(chargers.dataUpdatedAt)}</Typography>}
          </Box>
        }
      />

      {canEditDrivers && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 4 }}>
          <Button variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(DriverDialog, {})}>Add a driver</Button>
          <Button startIcon={<Link2 size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(OnboardingLinksDialog, {})}>Send an onboarding link</Button>
          <Button component={RouterLink} to="/sites">Change a price</Button>
          <Button component={RouterLink} to="/sessions">See this month's sessions</Button>
        </Box>
      )}

      {/* The strip of numbers: a near-black rule above, hairlines between. */}
      <StatStrip columns={5}>
        <Stat tone="success" label="Available" value={chargers.isPending ? null : counts.available} sub="Ready for a driver" />
        <Stat tone="live" label="Charging" value={chargers.isPending ? null : counts.charging} sub={counts.powerW ? `${(counts.powerW / 1000).toFixed(1)} kW drawn now` : 'Nobody charging'} />
        <Stat tone="fault" label="Faulted" value={chargers.isPending ? null : counts.faulted} sub={[...counts.faultedAt].join(', ') || 'None'} />
        <Stat tone="offline" label="Offline" value={chargers.isPending ? null : counts.offline} sub={[...counts.offlineAt].join(', ') || 'None'} />
        <Stat
          label="Energy this month"
          value={dash.data ? Number((dash.data.monthWh / 1000).toFixed(1)) : null}
          unit="kWh"
          sub={dash.data ? `Last month ${Math.round(dash.data.lastMonthWh / 1000).toLocaleString('en-AU')} kWh` : ' '}
        />
      </StatStrip>

      <ChargingByMonth />

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' }, alignItems: 'start' }}>
        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: `${radius.card}px`, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2, gap: 2 }}>
            <Typography variant="h2">Live sessions</Typography>
            <Link component={RouterLink} to="/sessions" sx={{ color: colours.nearBlack }}>See all sessions</Link>
          </Box>
          {chargers.isPending ? (
            <Skeleton variant="rounded" height={160} />
          ) : live.length === 0 ? (
            <Typography color="textSecondary" sx={{ py: 2 }}>Nobody is charging right now.</Typography>
          ) : phone ? (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {live.map(({ c, s }) => (
                <Box component="li" key={s.id} onClick={() => void dialog.open(SessionDialog, { sessionId: s.id })} sx={{ py: 1.5, borderTop: `1px solid ${colours.hairline}`, cursor: 'pointer' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontWeight: 500 }}>{whoCharged({ ...s, billingType: s.billingType as BillingType, cardLabel: null, receiptNumber: null }).title}</Typography>
                    <Typography className="num" sx={{ fontWeight: 500 }}>{kwhOf(s.energyWh)} kWh</Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {where(c)} · since {clock.format(new Date(s.startedAt))}{s.powerW ? ` · ${(s.powerW / 1000).toFixed(1)} kW` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto', mx: -1 }}>
              <Table size="small" aria-label="Live sessions" sx={{ '& td, & th': { px: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Driver and card</TableCell>
                    <TableCell>Where</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell align="right">Power</TableCell>
                    <TableCell align="right">kWh</TableCell>
                    <TableCell>Billed to</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {live.map(({ c, s }) => {
                    const who = whoCharged({ ...s, billingType: s.billingType as BillingType, cardLabel: null, receiptNumber: null });
                    return (
                      <TableRow key={s.id} hover onClick={() => void dialog.open(SessionDialog, { sessionId: s.id })} sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>{who.title}</Typography>
                          <Typography variant="body2" color="textSecondary">{who.sub}</Typography>
                        </TableCell>
                        <TableCell>{where(c)}</TableCell>
                        <TableCell className="num">{clock.format(new Date(s.startedAt))}</TableCell>
                        <TableCell align="right" className="num" sx={{ whiteSpace: 'nowrap' }}>{s.powerW ? `${(s.powerW / 1000).toFixed(1)} kW` : '—'}</TableCell>
                        <TableCell align="right" className="num" sx={{ fontWeight: 500 }}>{kwhOf(s.energyWh)}</TableCell>
                        <TableCell sx={{ color: colours.graphite }}>{billedTo({ billingType: s.billingType as BillingType, organisationName: c.organisationName, paidCents: null })}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        <Box sx={{ display: 'grid', gap: 3 }}>
          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: `${radius.card}px` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h2">Needs attention</Typography>
              {attention.length > 0 && (
                <Box component="span" className="num" sx={{ minWidth: 28, height: 28, px: 1, borderRadius: 999, bgcolor: colours.paleGrey, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500 }}>
                  {attention.length}
                </Box>
              )}
            </Box>
            {chargers.isPending ? (
              <Skeleton variant="rounded" height={120} />
            ) : attention.length === 0 ? (
              <Typography color="textSecondary" sx={{ py: 1 }}>Nothing. Every charger is working.</Typography>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {attention.slice(0, 6).map((a) => (
                  <Box component="li" key={a.key} sx={{ py: 2, borderTop: `1px solid ${colours.hairline}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <StatusLabel tone={a.tone}>{a.label}</StatusLabel>
                      {a.at && <Typography variant="body2" color="textSecondary">{formatAgo(a.at)}</Typography>}
                    </Box>
                    <Typography sx={{ fontWeight: 500, mt: 0.75 }}>{a.title}</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1.25 }}>{a.detail}</Typography>
                    {a.charger && <Button size="small" onClick={() => void dialog.open(ChargerDialog, { chargerId: a.charger!.id })}>Open charger</Button>}
                    {a.link && <Button size="small" component={RouterLink} to={a.link.to}>{a.link.text}</Button>}
                  </Box>
                ))}
                {attention.length > 6 && (
                  <Box component="li" sx={{ pt: 2, borderTop: `1px solid ${colours.hairline}` }}>
                    <Link component={RouterLink} to="/sites" sx={{ color: colours.nearBlack }}>{plural(attention.length - 6, 'more')} on Sites and chargers</Link>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: `${radius.card}px` }}>
            <Typography variant="h3" sx={{ mb: 2 }}>Saved this year</Typography>
            {dash.data ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Figure value={dash.data.yearCo2Kg >= 1000 ? (dash.data.yearCo2Kg / 1000).toFixed(1) : dash.data.yearCo2Kg.toFixed(0)} unit={dash.data.yearCo2Kg >= 1000 ? 't CO₂' : 'kg CO₂'} />
                <Figure value={Math.round(dash.data.yearFuelLitres).toLocaleString('en-AU')} unit="L of petrol" />
              </Box>
            ) : (
              <Skeleton variant="rounded" height={56} />
            )}
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1.5 }}>
              Against the same kilometres in a petrol car. {dash.data ? `This month ${dash.data.monthCo2Kg.toFixed(0)} kg CO₂.` : ''}
            </Typography>
          </Paper>
        </Box>
      </Box>
    </>
  );
}

/**
 * Chart colours from the design guidelines ("almost no colour"): near black and graphite. They
 * stay distinct for colour-blind readers (ΔE 37) and both have 3:1 or better on white.
 */
const CHART = { chargers: colours.nearBlack, home: colours.graphite };

/** The last 13 months of charging: sessions on chargers, plus home charging measured by Powersensor. */
function ChargingByMonth() {
  const sessions = useDashboardMonths();
  const home = useHomeChargingMonths(); // Powersensor is being retired: drop this series with it
  // Home charging only shows when an organisation has Powersensor turned on.
  const orgs = useOrganisations();
  const powersensorOn = (orgs.data ?? []).some((o) => o.powersensorEnabled);
  // Wait for every part, so the chart is drawn once with the real numbers rather than redrawn as each arrives.
  // (If the organisations or home charging can't load, the chart shows charger sessions only.)
  const loading = sessions.isPending || orgs.isPending || (powersensorOn && home.isPending);
  const months = sessions.data?.months.map((m) => m.month) ?? [];
  const series: ChartSeries[] = [
    { key: 'chargers', label: 'Charger sessions', color: CHART.chargers, values: Object.fromEntries((sessions.data?.months ?? []).map((m) => [m.month, m.wh / 1000])) },
    ...(powersensorOn ? [{ key: 'home', label: 'Home charging (Powersensor)', color: CHART.home, values: Object.fromEntries((home.data?.months ?? []).map((m) => [m.month, m.kwh])) }] : []),
  ];
  const total = months.reduce((t, m) => t + series.reduce((n, s) => n + (s.values[m] ?? 0), 0), 0);
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: `${radius.card}px`, mb: 3, position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="h2">Charging, last 13 months</Typography>
        {!loading && sessions.data && (
          <Typography color="textSecondary" className="num">
            {Math.round(total).toLocaleString('en-AU')} kWh in all
          </Typography>
        )}
      </Box>
      {sessions.error ? (
        <Typography color="textSecondary">The chart couldn't load. {sessions.error.message}</Typography>
      ) : (
        <MonthlyChargingChart months={months} series={series} loading={loading} />
      )}
    </Paper>
  );
}

function Figure({ value, unit }: { value: string; unit: string }) {
  return (
    <Box>
      <Typography className="num" sx={{ fontSize: 32, fontWeight: 500, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="body2" color="textSecondary">{unit}</Typography>
    </Box>
  );
}
